import Section from '@pelckmans/elementis/components/section';
import Tabs from '@pelckmans/elementis/components/tabs';
import Badge from '@pelckmans/elementis/components/badge';
import { Trans, useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback } from 'react';
import EmptyState from '../../../../shared-components/EmptyState';
import {
  SearchResultItem,
  SearchResultMedialink,
  SearchResultSlideSet,
  SearchResultToc,
  SearchResultUserMaterial,
} from './../api/search-api';
import LoadingState from '../../../../shared-components/LoadingState';
import SearchResultMedialinkItem from './SearchResultMedialinkItem';
import SearchResultTocItem from './SearchResultTocItem';
import { useSearchParams } from 'react-router-dom';
import SearchResultUserMaterialItem from './SearchResultUserMaterialItem';
import SearchResultSlideSetItem from './SearchResultSlideSetItem';

type SearchTab = 'all' | 'medialink' | 'toc' | 'slideset';

interface Props {
  results: SearchResultItem[] | null;
  isLoading: boolean;
}

function SearchResultList({ results, isLoading }: Props) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const initialTab: SearchTab = tabParam === 'medialink' || tabParam === 'toc' ? tabParam : 'all';

  const [selectedTab, setSelectedTab] = useState<SearchTab>(initialTab);

  const hasResults = results !== null && results.length > 0;
  const searchQuery = searchParams.get('q') || '';
  const trimmedSearchText = searchQuery.trim();

  const tabCounts = useMemo(() => {
    if (!results) return { all: 0, medialink: 0, toc: 0 };

    return {
      all: results.length,
      medialink: results.filter(item => item.docType === 'medialink' || item.docType === 'usermaterial').length,
      toc: results.filter(item => item.docType === 'toc').length,
      slideset: results.filter(item => item.docType === 'slideset').length,
    };
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!results) return null;

    if (selectedTab === 'all') return results;

    return results.filter(item => {
      const isMedialinkType = item.docType === 'medialink' || item.docType === 'usermaterial';
      const isTocType = item.docType === 'toc';
      const isSlideSetType = item.docType === 'slideset';

      return (
        (selectedTab === 'medialink' && isMedialinkType) ||
        (selectedTab === 'toc' && isTocType) ||
        (selectedTab === 'slideset' && isSlideSetType)
      );
    });
  }, [results, selectedTab]);

  const hasFilteredResults = filteredResults !== null && filteredResults.length > 0;

  const handleTabChange = useCallback(
    (tab: string) => {
      setSelectedTab(tab as SearchTab);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (tab === 'all') {
          newParams.delete('tab');
        } else {
          newParams.set('tab', tab);
        }
        return newParams;
      });
    },
    [setSearchParams],
  );

  return (
    <Tabs selectedTab={selectedTab} onTabChange={handleTabChange}>
      <Section
        title={t('PAGES.SEARCH.SECTION.TITLE')}
        filter={
          hasResults && !isLoading ? (
            <Tabs.TabList aria-label={t('PAGES.SEARCH.TABS.A11Y_LABEL')} size="sm">
              <Tabs.Tab id="all" badge={<Badge>{tabCounts.all}</Badge>}>
                {t('PAGES.SEARCH.TABS.ALL')}
              </Tabs.Tab>
              <Tabs.Tab id="medialink" badge={<Badge>{tabCounts.medialink}</Badge>}>
                {t('PAGES.SEARCH.TABS.MEDIALINK')}
              </Tabs.Tab>
              <Tabs.Tab id="toc" badge={<Badge>{tabCounts.toc}</Badge>}>
                {t('PAGES.SEARCH.TABS.TOC')}
              </Tabs.Tab>
              <Tabs.Tab id="slideset" badge={<Badge>{tabCounts.slideset}</Badge>}>
                {t('PAGES.SEARCH.TABS.SLIDE_SET')}
              </Tabs.Tab>
            </Tabs.TabList>
          ) : undefined
        }
      >
        {!isLoading && trimmedSearchText.length === 0 && (
          <EmptyState
            headerTitle={t('PAGES.SEARCH.EMPTY.TITLE')}
            bodyText={t('PAGES.SEARCH.EMPTY.BODY')}
            variant="list"
          />
        )}
        {!isLoading && !hasFilteredResults && trimmedSearchText.length > 0 && (
          <EmptyState
            headerTitle={t('PAGES.SEARCH.NO_RESULTS.TITLE')}
            bodyText={
              <span>
                <Trans
                  i18nKey="PAGES.SEARCH.NO_RESULTS.BODY"
                  values={{ searchTerm: trimmedSearchText }}
                  components={{ b: <b />, i: <i /> }}
                />
              </span>
            }
            variant="list"
          />
        )}
        {isLoading && <LoadingState variant="list" aria-label={t('PAGES.SEARCH.LOADING.A11Y_LABEL')} />}
        {!isLoading &&
          filteredResults?.map(item => {
            if (item.docType === 'toc') {
              return <SearchResultTocItem key={item.id} searchItem={item as SearchResultToc} />;
            }

            if (item.docType === 'usermaterial') {
              return <SearchResultUserMaterialItem key={item.id} searchItem={item as SearchResultUserMaterial} />;
            }

            if (item.docType === 'slideset') {
              return <SearchResultSlideSetItem key={item.id} searchItem={item as SearchResultSlideSet} />;
            }

            return <SearchResultMedialinkItem key={item.id} searchItem={item as SearchResultMedialink} />;
          })}
      </Section>
    </Tabs>
  );
}

export default SearchResultList;
